import { colors } from "@/styles/colors"
import { regexList } from "@/utils/regex"

type BackgroundAndColorReturn = {
	backgroundColor: string
	color: string
}

class ColorUtil {
	private readonly TYPOGRAPHY_BLACK = colors.gray[700]
	private readonly TYPOGRAPHY_WHITE = colors.gray[0]
	private readonly HEX_SHORT_FORMAT_LENGTH = 3
	private readonly HEX_PREFIX = "#"
	private readonly DEFAULT_THRESHOLD = 185
	private readonly GREEN_THRESHOLD = 205
	private readonly BLUE_THRESHOLD = 190
	private readonly PURPLE_THRESHHOLD = 160

	private sanitizeHexColor (hexColor: string): string {
		if (!hexColor || hexColor === "transparent") {
			return "FFFFFF"
		}

		let color = hexColor.replace(this.HEX_PREFIX, "")

		if (color.length === this.HEX_SHORT_FORMAT_LENGTH) {
			color += color
		}

		return color
	}

	private hexToRgb (hex: string) {
		const color = this.sanitizeHexColor(hex)
		const components = regexList.is_valid_hex_rgb_color.exec(this.HEX_PREFIX + color)

		if (!components) {
			return null
		}

		return {
			r: parseInt(components[1], 16),
			g: parseInt(components[2], 16),
			b: parseInt(components[3], 16)
		}
	}

	getContrastColor (hexColor: string): string {
		const rgbColor = this.hexToRgb(hexColor)

		if (!rgbColor) {
			return this.TYPOGRAPHY_BLACK
		}

		const { r, g, b } = rgbColor

		const hsp = Math.sqrt(
			0.299 * (r * r) +
            0.587 * (g * g) +
            0.114 * (b * b)
		)

		let threshold = this.DEFAULT_THRESHOLD

		if (g > r && g > b) {
			threshold = this.GREEN_THRESHOLD
		} else if (b > g && r > g) {
			threshold = this.PURPLE_THRESHHOLD
		} else {
			threshold = this.BLUE_THRESHOLD
		}

		return hsp > threshold ? this.TYPOGRAPHY_BLACK : this.TYPOGRAPHY_WHITE
	}

	getBackgroundAndContrastColor (backgroundColor: string): BackgroundAndColorReturn {
		return {
			backgroundColor: backgroundColor,
			color: this.getContrastColor(backgroundColor)
		}
	}

	hexToRgba (hexColor: string, opacity: number): string {
		const rgbColor = this.hexToRgb(hexColor)

		if (!rgbColor) {
			return hexColor
		}

		const { r, g, b } = rgbColor

		return `rgba(${r}, ${g}, ${b}, ${opacity})`
	}
}

export default new ColorUtil()
